/**
 * Turning the rendered SVG into a file. Everything here is browser-only.
 *
 * The raster formats go through a canvas: the SVG is handed to an <img> as a
 * data: URL, drawn once at the requested pixel size, and read back. A data:
 * URL rather than a blob: URL because the canvas must stay untainted — a
 * tainted canvas cannot be read back at all, and the logo the user picked is
 * itself already a data: URL inside the markup.
 */

export type RasterFormat = "png" | "jpeg" | "webp";

export const MIME: Record<RasterFormat, string> = {
  png: "image/png",
  jpeg: "image/jpeg",
  webp: "image/webp",
};

const EXTENSION: Record<RasterFormat, string> = {
  png: "png",
  jpeg: "jpg",
  webp: "webp",
};

export function svgDataUrl(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

async function loadSvg(svg: string): Promise<HTMLImageElement> {
  const image = new Image();
  image.src = svgDataUrl(svg);
  if (typeof image.decode === "function") {
    await image.decode();
    return image;
  }
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error("The image could not be drawn."));
  });
  return image;
}

/**
 * Rasterises the SVG at `size` x `size`.
 *
 * JPEG has no alpha channel, so a transparent code would come out on black.
 * `background` is painted underneath for that format (and only that format):
 * PNG and WebP keep whatever transparency the code was given.
 */
export async function rasterise(
  svg: string,
  format: RasterFormat,
  size: number,
  background: string | null,
): Promise<Blob> {
  const image = await loadSvg(svg);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("This browser would not give us a canvas to draw on.");
  if (format === "jpeg") {
    ctx.fillStyle = background ?? "#ffffff";
    ctx.fillRect(0, 0, size, size);
  }
  ctx.drawImage(image, 0, 0, size, size);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, MIME[format], 0.95),
  );
  // A browser that cannot write the format hands back null (or silently gives
  // PNG); WebP is the only one of the three where that is still plausible.
  if (!blob) throw new Error(`This browser cannot save ${format.toUpperCase()} images.`);
  return blob;
}

export function svgBlob(svg: string): Blob {
  return new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
}

export function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  // Revoking immediately can cancel the download in some browsers.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function filenameFor(base: string, format: RasterFormat | "svg"): string {
  return `${base}.${format === "svg" ? "svg" : EXTENSION[format]}`;
}

/** True when this browser can put an image on the clipboard at all. */
export function canCopyImage(): boolean {
  return typeof ClipboardItem !== "undefined" && typeof navigator.clipboard?.write === "function";
}

export async function copyPng(svg: string, size: number): Promise<void> {
  // The ClipboardItem is constructed with the *promise*, not an awaited blob:
  // Safari only honours a clipboard write started inside the click that asked
  // for it, and awaiting first puts the write in a later task.
  const item = new ClipboardItem({ "image/png": rasterise(svg, "png", size, null) });
  await navigator.clipboard.write([item]);
}

/**
 * Reads a picked logo into a data: URL, scaled down on the way in.
 *
 * Two reasons not to keep the original: it is embedded in every SVG this app
 * writes, so a 4 MB photo would make a 6 MB QR code; and the style is kept in
 * localStorage between visits, which a full-size image would not fit in. 512px
 * is more than the middle sixth of even a 2048px export can show.
 */
export async function readLogo(file: File, max = 512): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    await (typeof image.decode === "function"
      ? image.decode()
      : new Promise<void>((resolve, reject) => {
          image.onload = () => resolve();
          image.onerror = () => reject(new Error("That image could not be read."));
        }));
    const scale = Math.min(1, max / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("This browser would not give us a canvas to draw on.");
    ctx.drawImage(image, 0, 0, width, height);
    // Keep a photograph a photograph; anything that might have transparency
    // (and that includes every logo worth putting in the middle) becomes PNG.
    return file.type === "image/jpeg"
      ? canvas.toDataURL("image/jpeg", 0.92)
      : canvas.toDataURL("image/png");
  } finally {
    URL.revokeObjectURL(url);
  }
}
